import {
    useContext, useEffect, useMemo
} from 'react';
import _ from 'lodash';
import SgSpmPersonalDetails from 'sg-spm-personal-details';
import SgSpmAdditionalDriverOtherDetails from 'sg-spm-additional-driver-other-details';
import SgSpmDrivingHistory from 'sg-spm-driving-history';
import SgSpmClaimsAndConvictions from 'sg-spm-claims-and-convictions';
import SagaCommonMessages from 'sg-common-messages';
import FormAccordion from 'sg-form-accordion';
import { SG_AA_CTA_TEXT } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { WizardContext } from 'gw-portals-wizard-react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { SgScrollUtils } from 'saga-modules';
import metadata from './SgSpmAdditionalDriverForm.metadata.json5';
import {
    theirClaimAndConvictionsAccordionMetadata,
    theirDetailsAccordionMetadata, theirDrivingHistoryAccordionMetadata,
    theirOtherDetailsAccordionMetadata
} from './SpmAdditionalDriverPageAccordions';
import messages from './SgSpmAdditionalDriverForm.messages';

function SgSpmAdditionalDriverForm(props) {
    const {
        id,
        commonProps,
        handleCancel,
        setShowWizardContinue,
        showDuplicatedPersonError,
        retirementDateQuestionAvailable,
        additionalAccordions,
        onValidate: onValidateParent,
        driverIndex,
        handleNavigation,
        hasDoneScrollToClaims,
        setHasDoneScrollToClaims
    } = props;
    const { modelVM } = commonProps;
    const {
        onValidate,
        isComponentValid
    } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const wizardContext = useContext(WizardContext);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(false, id);
        }
        return () => {
            setTimeout(() => {
                if (!isComponentValid && !window.location.hash.includes(wizardContext.currentStep.path)) {
                    handleNavigation();
                }
            });
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
    }, []);

    const forceUpdateClaimsWithNewCodes = useMemo(() => { // CRF172 change, need to update claims with new codes
        const driver = _.get(modelVM, `lobData.privateMotor.coverables.spmDrivers.value[${driverIndex}]`);
        return driver.hasMotorClaims && _.isEmpty(driver.claims);
    }, []);

    const commonAccordions = [
        {
            name: 'About the additional driver(s)',
            accordionComponents: {
                spmAdditionalDriverPersonalDetailsAccordionComponent: {
                    name: 'spmAdditionalDriverPersonalDetailsAccordionComponent',
                    controller: SgSpmPersonalDetails,
                    componentProps: {
                        driverIndex,
                        showDuplicatedPersonError,
                        isPolicyHolder: false
                    }
                },
            },
            translations: {
                messages: messages.theirDetails,
                params: {
                    possessivePronoun: translator(SagaCommonMessages.theirPossessivePronoun),
                }
            },
            additionalNextValidation: () => {
                return showDuplicatedPersonError();
            },
            accordionMetadata: theirDetailsAccordionMetadata,
            nextButtonId: SG_AA_CTA_TEXT.DRIVERS_YOUR_DETAIL_NEXT
        },
        {
            name: 'Employment/spouse - additional driver',
            accordionComponents: {
                spmAdditionalDriverOtherPersonalDetailsAccordionComponent: {
                    name: 'spmAdditionalDriverOtherPersonalDetailsAccordionComponent',
                    controller: SgSpmAdditionalDriverOtherDetails,
                    componentProps: {
                        driverIndex,
                        retirementDateQuestionAvailable
                    }
                },
            },
            translations: {
                params: {
                    possessivePronoun: translator(SagaCommonMessages.theirPossessivePronoun),
                    pronoun: translator(SagaCommonMessages.theyPronoun)
                }
            },
            accordionMetadata: theirOtherDetailsAccordionMetadata,
            nextButtonId: SG_AA_CTA_TEXT.DRIVERS_YOUR_EMPLOYMENT_NEXT
        },
        {
            name: 'Driving history - additional driver',
            accordionComponents: {
                SpmDrivingHistoryAccordionComponent: {
                    name: 'spmDrivingHistoryAccordionComponent',
                    controller: SgSpmDrivingHistory,
                    componentProps: {
                        driverIndex
                    }
                },
            },
            translations: {
                messages: messages.theirDrivingHistory,
                params: {
                    possessivePronoun: translator(SagaCommonMessages.theirPossessivePronoun),
                    pronoun: translator(SagaCommonMessages.theyPronoun),
                }
            },
            accordionMetadata: theirDrivingHistoryAccordionMetadata,
            nextButtonId: SG_AA_CTA_TEXT.DRIVERS_DRIVING_HISTORY_NEXT
        },
        {
            name: 'Claims and convictions - additional driver',
            accordionComponents: {
                SgSpmClaimsAndConvictions: {
                    name: 'SgSpmClaimsAndConvictions',
                    controller: SgSpmClaimsAndConvictions,
                    componentProps: {
                        path: `lobData.privateMotor.coverables.spmDrivers.children[${driverIndex}]`
                    }
                }
            },
            translations: {
                messages: messages.theirClaimsAndConvictions,
                params: {
                    possessivePronoun: translator(SagaCommonMessages.theirPossessivePronoun),
                    pronoun: translator(SagaCommonMessages.theyPronoun)
                }
            },
            accordionMetadata: theirClaimAndConvictionsAccordionMetadata,
            customProps: {
                openOnLoad: forceUpdateClaimsWithNewCodes
            }
        },
    ];

    const accordions = [...commonAccordions, ...additionalAccordions];

    if (forceUpdateClaimsWithNewCodes && !hasDoneScrollToClaims) {
        const canScroll = _.every(accordions, (acc, index) => {
            return !_.isNil(document.getElementById(`card${index}`));
        });
        if (canScroll) {
            SgScrollUtils.scrollToElementById('card3', 500);
            setHasDoneScrollToClaims(true);
        }
    }

    const overrideProps = {
        formAccordionComponent: {
            commonProps,
            accordions,
            setShowWizardContinue,
            handleCancel
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate,
        },
        resolveComponentMap: {
            FormAccordion
        }
    };

    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

SgSpmAdditionalDriverForm.defaultProps = {
    additionalAccordions: [],
    handleCancel: { action: () => {} },
    handleNavigation: () => {},
};

export default SgSpmAdditionalDriverForm;
